import { useState } from "react";

import {
  faCheck,
  faChevronDown,
  faUser,
  faUserCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { isObject } from "lodash-es";
import { AnimatePresence } from "motion/react";
import { twJoin } from "tailwind-merge";

import { Input } from "src/base-components/Input";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { OrganizationUser } from "src/clients/taktile-api";
import { Avatar } from "src/design-system/Avatar";
import { Button } from "src/design-system/Button";
import { DropdownItem } from "src/design-system/Dropdown";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Skeleton } from "src/design-system/Skeleton";
import { useFuseSearch } from "src/utils/useFuseSearch";
import { canUserBeAssignee, getUserName } from "src/utils/user";

type AssigneeOption = {
  key: string;
  value: OrganizationUser;
};

const UNSET_OPTION = { key: "unset", value: "Unset user" } as const;

const SEARCH_OPTIONS = {
  keys: ["value.full_name", "value.username", "value.email"],
  threshold: 0.3,
};

const isUser = (value: unknown): value is OrganizationUser =>
  isObject(value) && "role" in value;

type AssigneeSelectBaseProps = {
  value?: Nullable<string>;
  users: OrganizationUser[];
  workspaceId: string;
  onChange: (value: Nullable<string>) => void;
  disabled?: boolean;
  isLoading?: boolean;
  dataLoc?: string;
};

type PillVariantProps = AssigneeSelectBaseProps & {
  variant: "pill";
};

type InlineVariantProps = AssigneeSelectBaseProps & {
  variant: "inline";
  isInRow?: boolean;
};

type AssigneeSelectProps = PillVariantProps | InlineVariantProps;

export const AssigneeSelect: React.FC<AssigneeSelectProps> = (props) => {
  const {
    value,
    users,
    workspaceId,
    onChange,
    disabled = false,
    isLoading = false,
    dataLoc,
    variant,
  } = props;

  const [query, setQuery] = useState("");

  const assigneeOptions: AssigneeOption[] = users
    .filter((user) => canUserBeAssignee(workspaceId, user))
    .map((user) => ({ key: user.id, value: user }));

  const options: (AssigneeOption | typeof UNSET_OPTION)[] =
    assigneeOptions.length > 0 ? [UNSET_OPTION, ...assigneeOptions] : [];

  const search = useFuseSearch(options, SEARCH_OPTIONS);
  const results = search(query);
  const selectedOption = options.find((option) => option.key === value) ?? null;
  const selectedUser = users.find((user) => user.id === value);

  return (
    <Listbox
      disabled={disabled}
      value={selectedOption}
      onChange={(option) => {
        if (!option) return;

        if (option.key === UNSET_OPTION.key || option.key === value) {
          onChange(null);
        } else {
          onChange(option.key);
        }
      }}
    >
      {({ open }) => (
        <>
          <ListboxButton
            as={variant === "pill" ? "div" : "button"}
            className="flex"
            data-loc={dataLoc}
            disabled={disabled}
          >
            {variant === "pill" ? (
              <PillTrigger disabled={disabled} user={selectedUser} />
            ) : (
              <InlineTrigger
                isInRow={"isInRow" in props ? props.isInRow : undefined}
                open={open}
                user={selectedUser}
              />
            )}
          </ListboxButton>
          <AnimatePresence initial={false} onExitComplete={() => setQuery("")}>
            {open && (
              <ListboxOptions
                anchor={{
                  to: "bottom start",
                  offset: 8,
                  padding: 8,
                  gap: 6,
                }}
                as={MotionDropdownPanel}
                className="min-h-[18rem] min-w-[max(var(--button-width),17rem)] max-w-80"
                data-loc={dataLoc ? `${dataLoc}-options` : undefined}
                static
              >
                <div className="px-4 py-2">
                  <Input
                    ref={(node) => node?.focus()}
                    disabled={isLoading}
                    placeholder="Search user..."
                    prefixIcon={{ icon: faSearch }}
                    role="textbox"
                    value={query}
                    fullWidth
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <div className="decideScrollbar max-h-60 overflow-y-auto">
                  {isLoading ? (
                    Array.from({ length: 6 }, (_, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-x-2 px-4 py-2 font-inter-normal-13px"
                      >
                        <Skeleton
                          height="h-4.5"
                          rounded="rounded-full"
                          width="w-4.5"
                        />
                        <div className="flex-auto">
                          <Skeleton height="h-4" width="w-full" />
                        </div>
                      </div>
                    ))
                  ) : results.length > 0 ? (
                    <ul>
                      {results.map((item) => (
                        <ListboxOption
                          key={item.key}
                          as={DropdownItem}
                          value={item}
                        >
                          <AssigneeItem isSelected={!value} item={item.value} />
                        </ListboxOption>
                      ))}
                    </ul>
                  ) : query ? (
                    <EmptyState
                      description="Change your search query to try again"
                      headline="No users found"
                      icon={faUser}
                    />
                  ) : (
                    <EmptyState
                      description="No users are available for assignment"
                      headline="No assignees"
                      icon={faUser}
                    />
                  )}
                </div>
              </ListboxOptions>
            )}
          </AnimatePresence>
        </>
      )}
    </Listbox>
  );
};

const PillTrigger: React.FC<{
  user?: OrganizationUser;
  disabled: boolean;
}> = ({ user, disabled }) => (
  <div
    className={twJoin(
      "flex max-w-42 items-center truncate",
      !disabled && "cursor-pointer",
    )}
  >
    {user ? (
      <Pill fullWidth size="sm" variant="gray">
        <Pill.Avatar user={user} />
        <Pill.Text>{getUserName(user)}</Pill.Text>
        {!disabled && <Pill.Icon icon={faChevronDown} />}
      </Pill>
    ) : (
      <Button
        isDisabled={disabled}
        iconLeft={faUser}
        iconRight={faChevronDown}
        size="sm"
        variant="secondary"
      >
        Select assignee
      </Button>
    )}
  </div>
);

const InlineTrigger: React.FC<{
  user?: OrganizationUser;
  isInRow?: boolean;
  open: boolean;
}> = ({ user, isInRow, open }) => (
  <div className="flex items-center gap-x-2 truncate text-left font-inter-normal-12px text-gray-800">
    {user ? (
      <>
        <Avatar user={user} />
        <p className="truncate">{getUserName(user)}</p>
      </>
    ) : (
      <>
        <span
          className={twJoin(
            isInRow && "group-hover/row:inline",
            "truncate text-gray-500 italic",
            isInRow && !open && "hidden",
            (!isInRow || open) && "inline",
          )}
        >
          Select Assignee
        </span>
        <span
          className={twJoin(
            isInRow && "group-hover/row:hidden",
            isInRow && !open && "inline",
            (!isInRow || open) && "hidden",
          )}
        >
          -
        </span>
      </>
    )}
    <div
      className={twJoin(
        isInRow && "group-hover/row:visible",
        isInRow && !open && "invisible",
        (!isInRow || open) && "visible",
      )}
    >
      <Icon icon={faChevronDown} size="3xs" />
    </div>
  </div>
);

const AssigneeItem: React.FC<{
  item: OrganizationUser | string;
  isSelected: boolean;
}> = ({ item, isSelected }) =>
  isUser(item) ? (
    <div className="flex w-full items-center gap-x-2 pr-2 font-inter-normal-13px text-gray-800">
      <div className="p-1">
        <Avatar user={item} />
      </div>
      <p className="truncate">{getUserName(item)}</p>
    </div>
  ) : (
    <div className="flex w-full items-center gap-x-2 font-inter-normal-13px text-gray-800">
      <div className="px-px">
        <Icon icon={faUserCircle} size="sm" />
      </div>
      No assignee
      {isSelected && (
        <div className="ml-auto">
          <Icon icon={faCheck} size="sm" variant="active" />
        </div>
      )}
    </div>
  );
